/**
 * EdgeSheet — a modal sheet that leaves one edge of the game screen showing:
 *
 *   keepTopVisible   portrait: the window y of the top bar's bottom edge. The
 *                    top bar stays visible (greyed) and the sheet fills below.
 *   keepLeftVisible  wide layouts: the side column's width. It stays visible
 *                    (greyed) and the sheet covers the map area beside it.
 *   neither          a plain full-screen modal that slides up.
 *
 * With a kept edge the sheet drives its own entrance: it slides up while the
 * shade over the kept edge fades in on the same timing (rather than the shade
 * riding up with the sheet), and the modal stays mounted until the exit slide
 * has finished. Tapping the shade closes. Used by the cards modal and the
 * grown game log.
 */
import React from 'react'
import { Animated, Easing, Pressable, View, useWindowDimensions } from 'react-native'
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context'
import { useReducedMotion } from 'react-native-reanimated'
import { AppModal } from './AppModal'

/** Sheet slide with a kept edge (about RN's own modal slide). */
export const EDGE_SHEET_SLIDE_MS = 300
// Visual-fixture builds capture still frames: no time-based entrance.
const VISUAL_FIXTURES = process.env.EXPO_PUBLIC_VISUAL_FIXTURES === '1'

function useSheetTransition(visible: boolean, enabled: boolean, onClosed?: () => void) {
  const reducedMotion = useReducedMotion()
  const progress = React.useRef(new Animated.Value(visible ? 1 : 0)).current
  const [mounted, setMounted] = React.useState(visible)
  const onClosedRef = React.useRef(onClosed)
  onClosedRef.current = onClosed
  // Once the slide has had its time the sheet switches to static values
  // (`settledOpen`) instead of trusting the animated value to land: a view can
  // stop following its animation (React's dev double-mount detaches it on
  // web), which left the sheet parked off-screen. Closing likewise unmounts on
  // a timer rather than on the animation's callback.
  const [settledOpen, setSettledOpen] = React.useState(visible)
  const first = React.useRef(true)
  React.useEffect(() => {
    const initial = first.current
    first.current = false
    const closed = () => {
      setMounted(false)
      if (!initial) onClosedRef.current?.()
    }
    if (!enabled) {
      if (!visible && !initial) onClosedRef.current?.()
      return
    }
    if (visible) setMounted(true)
    setSettledOpen(false)
    if (reducedMotion || VISUAL_FIXTURES) {
      progress.setValue(visible ? 1 : 0)
      if (visible) setSettledOpen(true)
      else closed()
      return
    }
    progress.setValue(visible ? 0 : 1)
    const animation = Animated.timing(progress, {
      toValue: visible ? 1 : 0,
      duration: EDGE_SHEET_SLIDE_MS,
      easing: visible ? Easing.out(Easing.cubic) : Easing.in(Easing.cubic),
      useNativeDriver: true,
    })
    animation.start()
    const done = setTimeout(() => {
      if (visible) setSettledOpen(true)
      else closed()
    }, EDGE_SHEET_SLIDE_MS)
    return () => {
      animation.stop()
      clearTimeout(done)
    }
  }, [visible, enabled, reducedMotion, progress])
  return { progress, settledOpen, mounted: enabled ? mounted || visible : visible }
}

export function EdgeSheet({
  visible,
  onRequestClose,
  closeAccessibilityLabel,
  testIDPrefix,
  keepTopVisible = null,
  keepLeftVisible = null,
  onClosed,
  children,
}: {
  visible: boolean
  onRequestClose: () => void
  /** The shade's label (it closes the sheet). */
  closeAccessibilityLabel: string
  /** Shade test ids: `<prefix>-top-shade` / `<prefix>-left-shade`. */
  testIDPrefix: string
  keepTopVisible?: number | null
  keepLeftVisible?: number | null
  /** After the exit slide has finished (e.g. to open something under it). */
  onClosed?: () => void
  children: React.ReactNode
}) {
  const { height: screenHeight } = useWindowDimensions()
  const keepTop = keepTopVisible !== null
  const keepLeft = !keepTop && keepLeftVisible !== null
  const keepEdge = keepTop || keepLeft
  const sheet = useSheetTransition(visible, keepEdge, onClosed)
  const shadeOpacity = { opacity: sheet.settledOpen ? 1 : sheet.progress }

  return (
    <AppModal
      preserveGameTopBar={false}
      visible={sheet.mounted}
      animationType={keepEdge ? 'none' : 'slide'}
      transparent={keepEdge}
      onRequestClose={onRequestClose}
    >
      {/* SafeAreaProvider INSIDE the modal: RN <Modal> renders in its own
          portal outside the app's root provider, so without this the
          SafeAreaView resolves a 0 top inset and the header is clipped under
          the notch/status bar in portrait. */}
      <SafeAreaProvider>
        <View style={{ flex: 1, flexDirection: keepLeft ? 'row' : 'column' }}>
          {keepLeft && (
            <Animated.View style={shadeOpacity}>
              <Pressable
                testID={`${testIDPrefix}-left-shade`}
                accessibilityRole="button"
                accessibilityLabel={closeAccessibilityLabel}
                onPress={onRequestClose}
                style={{ width: keepLeftVisible ?? 0, height: '100%', backgroundColor: 'rgba(0,0,0,0.35)' }}
              />
            </Animated.View>
          )}
          {keepTop && (
            <Animated.View style={shadeOpacity}>
              <Pressable
                testID={`${testIDPrefix}-top-shade`}
                accessibilityRole="button"
                accessibilityLabel={closeAccessibilityLabel}
                onPress={onRequestClose}
                style={{ height: keepTopVisible, backgroundColor: 'rgba(0,0,0,0.35)' }}
              />
            </Animated.View>
          )}
          <Animated.View
            style={keepEdge ? {
              flex: 1,
              transform: [{ translateY: sheet.settledOpen ? 0 : sheet.progress.interpolate({ inputRange: [0, 1], outputRange: [screenHeight, 0] }) }],
            } : { flex: 1 }}
          >
            <SafeAreaView
              // Paper like the player details drawer; under a kept edge the
              // sheet gets the drawer's olive edge.
              className={`flex-1 bg-paper ${keepTop ? 'border-t-2 border-olive' : keepLeft ? 'border-l-2 border-olive' : ''}`}
              edges={keepTop ? ['bottom', 'left', 'right'] : keepLeft ? ['top', 'bottom', 'right'] : undefined}
            >
              {children}
            </SafeAreaView>
          </Animated.View>
        </View>
      </SafeAreaProvider>
    </AppModal>
  )
}
